import React from "react";
import { Link } from "react-router-dom";
import { Globe2, Plane, Languages, Briefcase, Award, Users, Radio, Sparkles, CheckCircle2, Calendar, MapPin, Wallet } from "lucide-react";
import { PageHero, Section, Card } from "@/components/Section";
import { IMAGES } from "@/lib/site";

const objectifs = [
  { icon: Plane, t: "Accroître la mobilité des jeunes", d: "Une première expérience à l'étranger pour gagner en autonomie et en confiance." },
  { icon: Languages, t: "Compétences linguistiques", d: "Améliorer la maîtrise d'une langue étrangère en l'utilisant au quotidien." },
  { icon: Globe2, t: "Ouverture interculturelle", d: "Découvrir une culture et des savoir-faire différents, élargir son regard." },
  { icon: Briefcase, t: "Pratiques professionnelles", d: "Confronter ses compétences à d'autres méthodes de travail européennes." },
  { icon: Sparkles, t: "Sentiment européen", d: "Développer le sentiment d'appartenance à l'Union européenne." },
  { icon: Award, t: "Insertion professionnelle", d: "Favoriser une meilleure insertion sur le marché du travail à la sortie du lycée." },
];

const conditions = [
  { icon: Calendar, t: "Durée du séjour", v: "4 semaines obligatoires à l'étranger." },
  { icon: Users, t: "Qui peut partir ?", v: "Priorité aux élèves de section européenne (DNL), proposition étendue aux élèves de Première ASSP." },
  { icon: MapPin, t: "Destinations", v: "Irlande, Espagne, et autres pays européens sous réserve d'une recherche individuelle et d'une solution d'hébergement validée." },
  { icon: Wallet, t: "Participation financière", v: "Participation moyenne des familles : 600 €. Le lycée complète grâce aux financements Erasmus+. Des actions de financement peuvent être menées par les élèves retenus." },
];

const criteres = ["Motivation", "Implication dans le projet", "Autonomie", "Attitude en cours, au lycée et en stage", "Niveau de langue"];

const engagements = [
  "Versement d'un chèque d'engagement de 200 € (mi-novembre).",
  "Aucun désistement possible après l'attribution de la bourse.",
  "Participation obligatoire aux ateliers Erasmus+.",
  "Réalisation du test linguistique OLS avant le départ et au retour.",
  "Rédaction du bilan de stage demandé par l'Agence Erasmus+.",
];

const ambassadeur = [
  "Témoigner lors des Journées Portes Ouvertes.",
  "Alimenter les réseaux sociaux de l'établissement avec photos, vidéos et témoignages.",
  "Partager leur expérience avec les futurs candidats.",
];

const benefices = [
  "Une expérience humaine et professionnelle enrichissante",
  "Le diplôme Europass Mobilité",
  "L'option mobilité avec points supplémentaires au baccalauréat",
  "Une valorisation sur Parcoursup",
  "La validation de la compétence mobilité sur l'attestation de réussite intermédiaire",
  "Un CV renforcé pour les élèves de lycée professionnel",
  "Une ouverture culturelle européenne",
  "Le développement d'un réseau personnel et professionnel",
  "Un gain en autonomie",
  "De meilleures compétences en langue étrangère",
];

export default function Erasmus() {
  return (
    <div data-testid="erasmus-page">
      <PageHero
        eyebrow="Erasmus+ · Mobilité européenne"
        title="Ouvrir les horizons de nos élèves"
        subtitle="Labellisé Erasmus+, notre lycée offre à ses élèves l'opportunité de développer leurs compétences et leur employabilité en réalisant une partie de leurs études à l'étranger. Véritable chance d'ouverture sur l'Europe, le programme favorise la mobilité, le renforcement des compétences linguistiques et l'élargissement des pratiques professionnelles."
        image={IMAGES.partenaires}
      />

      <Section eyebrow="Pourquoi participer ?" title="Une expérience qui change tout">
        <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {objectifs.map((o, i) => (
            <Card key={o.t} data-testid={`erasmus-objectif-${i}`}>
              <span className="grid h-12 w-12 place-items-center rounded-2xl bg-brand-sand/30 text-brand-slate-dark">
                <o.icon className="h-5 w-5" />
              </span>
              <h3 className="mt-5 font-heading text-lg font-semibold text-brand-slate-dark">{o.t}</h3>
              <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{o.d}</p>
            </Card>
          ))}
        </div>
      </Section>

      <Section
        wrapper="bg-brand-sand-wrapper/40"
        eyebrow="Conditions de participation"
        title="Tout ce qu'il faut savoir avant de candidater"
      >
        <div className="grid gap-6 sm:grid-cols-2">
          {conditions.map((c, i) => (
            <Card key={c.t} data-testid={`erasmus-condition-${i}`}>
              <div className="flex items-start gap-4">
                <span className="grid h-11 w-11 shrink-0 place-items-center rounded-2xl bg-brand-sage/15 text-brand-sage-dark">
                  <c.icon className="h-5 w-5" />
                </span>
                <div>
                  <h3 className="font-heading text-base font-semibold text-brand-slate-dark">{c.t}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{c.v}</p>
                </div>
              </div>
            </Card>
          ))}
        </div>
      </Section>

      <Section eyebrow="Sélection des candidats" title="Les critères pris en compte">
        <div className="grid gap-12 lg:grid-cols-2">
          <div>
            <ul className="space-y-3">
              {criteres.map((c) => (
                <li key={c} className="flex items-start gap-3 rounded-2xl border border-black/5 bg-brand-cream p-4">
                  <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-brand-sage-dark" />
                  <span className="text-sm font-medium text-brand-slate-dark">{c}</span>
                </li>
              ))}
            </ul>
            <p className="mt-6 rounded-2xl bg-brand-sand-wrapper/60 p-5 text-sm text-muted-foreground">
              Les élèves retenus et leurs représentants légaux signent un{" "}
              <strong className="text-brand-slate-dark">contrat d'engagement</strong> précisant les
              droits et devoirs de chacun pour la réussite du séjour.
            </p>
          </div>

          <Card data-testid="erasmus-engagements">
            <h3 className="font-heading text-xl font-bold text-brand-slate-dark">Après la sélection</h3>
            <p className="mt-2 text-sm text-muted-foreground">
              Les bénéficiaires s'engagent à respecter le cadre suivant :
            </p>
            <ul className="mt-5 space-y-3 text-sm">
              {engagements.map((e) => (
                <li key={e} className="flex gap-3 text-brand-slate-dark">
                  <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-brand-sand-dark" />
                  <span className="text-muted-foreground">{e}</span>
                </li>
              ))}
            </ul>
          </Card>
        </div>
      </Section>

      <Section
        wrapper="bg-brand-slate text-brand-cream"
        eyebrow="Devenir ambassadeur"
        title="Transmettre son expérience après le retour"
      >
        <div className="grid gap-12 lg:grid-cols-[1.2fr_1fr] lg:items-center">
          <div>
            <p className="text-base leading-relaxed text-brand-cream/85">
              Au retour de leur séjour, les élèves bénéficiaires deviennent ambassadeurs du programme
              Erasmus+ au sein de l'établissement. Cet engagement permet de partager l'expérience et
              de donner envie aux futures promotions de tenter l'aventure.
            </p>
            <ul className="mt-6 space-y-3">
              {ambassadeur.map((a) => (
                <li key={a} className="flex items-start gap-3 text-sm">
                  <Sparkles className="mt-0.5 h-4 w-4 shrink-0 text-brand-sand" />
                  <span className="text-brand-cream/90">{a}</span>
                </li>
              ))}
            </ul>
          </div>
          <div className="overflow-hidden rounded-3xl">
            <img src={IMAGES.values} alt="Élèves Erasmus+" className="h-full w-full object-cover" />
          </div>
        </div>
      </Section>

      <Section eyebrow="Les bénéfices" title="Ce que l'on retient de l'expérience">
        <div className="rounded-3xl border border-brand-sand/40 bg-brand-sand-wrapper/40 p-8 lg:p-12">
          <div className="grid gap-3 sm:grid-cols-2">
            {benefices.map((b, i) => (
              <div
                key={b}
                data-testid={`erasmus-benefice-${i}`}
                className="flex items-start gap-3 rounded-2xl bg-brand-cream p-4"
              >
                <Award className="mt-0.5 h-5 w-5 shrink-0 text-brand-sand-dark" />
                <span className="text-sm font-medium text-brand-slate-dark">{b}</span>
              </div>
            ))}
          </div>
        </div>
      </Section>

      <Section wrapper="bg-brand-sand-wrapper/40">
        <div className="grid items-center gap-10 lg:grid-cols-[1fr_1.3fr]">
          <div className="overflow-hidden rounded-3xl">
            <img src={IMAGES.classroom} alt="Erasmus Days" className="h-full w-full object-cover" />
          </div>
          <div>
            <div className="inline-flex items-center gap-3 rounded-full bg-brand-slate/10 px-4 py-2 text-sm font-semibold text-brand-slate-dark">
              <Radio className="h-4 w-4" /> Erasmus Days
            </div>
            <h2 className="mt-4 font-heading text-3xl font-bold tracking-tight text-brand-slate-dark sm:text-4xl">
              Un podcast pour faire vivre l'aventure européenne
            </h2>
            <p className="mt-5 text-base leading-relaxed text-muted-foreground">
              Chaque année, nos élèves participent aux Erasmus Days en partageant leurs expériences et
              leurs découvertes à travers un podcast diffusé sur Joki Radio. Une manière vivante et
              moderne de transmettre la richesse de cette mobilité.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link
                to="/contact"
                data-testid="erasmus-cta-contact"
                className="inline-flex items-center gap-2 rounded-full bg-brand-slate px-6 py-3.5 text-sm font-semibold text-brand-cream transition-all duration-300 hover:-translate-y-0.5 hover:bg-brand-slate-dark"
              >
                Contacter l'équipe Erasmus
              </Link>
              <Link
                to="/formations"
                data-testid="erasmus-cta-formations"
                className="inline-flex items-center gap-2 rounded-full border border-brand-slate/20 px-6 py-3.5 text-sm font-semibold text-brand-slate-dark transition-all duration-300 hover:bg-brand-slate hover:text-brand-cream"
              >
                Découvrir les formations éligibles
              </Link>
            </div>
            <p className="mt-6 rounded-2xl border border-brand-sand/40 bg-brand-cream p-5 text-sm font-medium text-brand-slate-dark">
              Envie de vivre une expérience unique en Europe ? Renseignez-vous auprès de l'équipe
              Erasmus du lycée.
            </p>
          </div>
        </div>
      </Section>
    </div>
  );
}
